import { useMemo } from 'react'
import { Link, Navigate, Outlet, useLocation, useNavigation } from 'react-router-dom'
import { LogOut, User } from 'lucide-react'
import { createClient } from '@/lib/auth/client'
import { apiClient } from '@/lib/api/client'
import { useAsyncData } from '@/src/hooks/useAsyncData'
import { Button } from '@/components/ui/button'
import { ThemeToggle } from '@/components/theme-toggle'
import { NotificationBell } from '@/src/components/NotificationBell'
import { ReportProblem } from '@/src/components/ReportProblem'
import { InstallHelm } from '@/src/components/InstallHelm'
import { XtellarMark } from '@/src/components/XtellarMark'
import { EmailConfirmationBanner } from '@/src/components/EmailConfirmationBanner'
import { CustomerSuspensionBoundary } from '@/src/components/AccountSuspended'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarTrigger,
} from '@/components/ui/sidebar'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { getHelmLoginUrl } from '@/src/lib/auth'
import { AuthGate } from './AuthGate'
import { helmAccess } from '@/src/lib/roles'
import { navigationFor } from '@/src/lib/navigation'
import { SidebarNav } from './SidebarNav'
import { AssistantWidget } from '@/src/components/assistant/AssistantWidget'
import { SidebarWithOnboarding } from '@/src/components/onboarding/Onboarding'
import { HelpMenu } from '@/src/components/onboarding/HelpMenu'
import { TranslationProvider } from '@/src/components/translation/TranslationProvider'
import { TranslatePageMenu } from '@/src/components/translation/TranslatePageMenu'

function RouteProgress() {
  const navigation = useNavigation()

  if (navigation.state === 'idle') {
    return null
  }

  return (
    <div className="absolute inset-x-0 bottom-0 h-0.5 overflow-hidden bg-muted">
      <div className="h-full w-1/3 animate-[route-progress_1s_ease-in-out_infinite] bg-primary" />
    </div>
  )
}

/**
 * The warehouse team sees the warehouse and nothing else; the Warehouse item
 * shows only to them and to admins. Parcels, a customer's own page for sending
 * to the warehouse, shows only to customer logins.
 */
function useNavigationFor() {
  const profile = useAsyncData(() => apiClient.getProfile().catch(() => null), [])
  const access = useMemo(() => (profile.data ? helmAccess(profile.data) : null), [profile.data])
  const { warehouseOnly, customer } = access ?? helmAccess(null)
  return {
    access,
    warehouseOnly,
    customer,
    // Beacon is in every menu but the warehouse team's; its widget follows the
    // menu, and is bound to the person whose conversations it holds.
    assistantUserId: profile.data && !warehouseOnly ? profile.data.id : null,
    ...navigationFor(access),
  }
}

export function AppShell() {
  const location = useLocation()
  const authClient = useMemo(() => createClient(), [])
  const navigation = useNavigationFor()

  const handleLogout = async () => {
    await authClient.auth.signOut()
    window.location.href = getHelmLoginUrl('/')
  }

  return (
    <AuthGate>
      {(user) => (
        // Translation is per person: their language, their page toggle.
        <TranslationProvider key={user.id} userId={user.id}>
          <CustomerSuspensionBoundary onSignOut={handleLogout}>
            <SidebarWithOnboarding userId={user.id} access={navigation.access}>
              <div className="flex min-h-screen w-full">
                <Sidebar>
                  <SidebarContent>
                    <SidebarGroup>
                      <SidebarGroupLabel className="px-4 py-6 text-xl font-bold">
                        <Link to="/" className="flex items-center gap-2">
                          <XtellarMark />
                          Helm
                        </Link>
                      </SidebarGroupLabel>
                    </SidebarGroup>
                    <SidebarNav
                      userId={user.id}
                      pathname={location.pathname}
                      navigation={navigation}
                    />
                  </SidebarContent>
                  <SidebarFooter className="border-t p-4">
                    <InstallHelm />
                    <Button onClick={handleLogout} variant="ghost" className="w-full justify-start">
                      <LogOut className="mr-2 h-4 w-4" />
                      Logout
                    </Button>
                  </SidebarFooter>
                </Sidebar>

                <div className="flex min-w-0 flex-1 flex-col">
                  <header className="relative border-b bg-background">
                    <div className="flex h-16 items-center gap-3 px-4 md:gap-4 md:px-6">
                      <SidebarTrigger data-tour="nav-menu" />
                      <div className="ml-auto flex items-center gap-1 sm:gap-2 md:gap-4">
                        <TranslatePageMenu />
                        <HelpMenu />
                        <ReportProblem />
                        <NotificationBell />
                        <ThemeToggle />
                        <Avatar>
                          <AvatarFallback>
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                    <RouteProgress />
                  </header>
                  {/* 24px each side is a tenth of a phone screen before any page draws
                  anything. Every page reads better tighter on mobile; desktop
                  keeps the room it had. */}
                  <main className="flex-1 bg-muted/40 p-3 md:p-6">
                    {navigation.customer && <EmailConfirmationBanner />}
                    {navigation.warehouseOnly && !location.pathname.startsWith('/warehouse') ? (
                      <Navigate to="/warehouse" replace />
                    ) : (
                      <Outlet />
                    )}
                    {navigation.assistantUserId && (
                      <AssistantWidget userId={navigation.assistantUserId} />
                    )}
                  </main>
                </div>
              </div>
            </SidebarWithOnboarding>
          </CustomerSuspensionBoundary>
        </TranslationProvider>
      )}
    </AuthGate>
  )
}
